import {
  PatientWithAccessRightDTO,
  PaginatedPatientsDTO,
} from '@peartherapeutics/weaver-api-client';
import React, { useEffect, useState } from 'react';
import { bindActionCreators, Dispatch } from 'redux';
import { connect } from 'react-redux';
import { match } from 'react-router';
import { Location, History } from 'history';

import { clearCurrentPatient } from 'actions/currentPatient';
import {
  getPatientDetailsListSuccess,
  getPatientDetailsListError,
} from 'actions/patientDetailsList';
import { WeaverApiClientInstance } from 'api/weaverApiClientInstance';
import useDebounce from 'custom-hooks/useDebounce';
import withSort from 'components/common/Sort';
import ErrorInterceptor from 'components/common/ErrorInterceptor';

import PatientsTable from './PatientsTable';
import SearchInput, { DEBOUNCE_TIMEOUT } from './SearchInput';
import './styles.scss';
import Pagination from 'components/common/Pagination';

export interface IDispatchProps {
  getPatientDetailsListSuccess: (payload: PatientWithAccessRightDTO[]) => void;
  getPatientDetailsListError: () => void;
  clearCurrentPatient: () => void;
}

export interface IOwnProps {
  history: History;
  location: Location;
  match: match<{ [key: string]: string }>;
  sortProp: Nullable<string>;
  sortOrder: Nullable<string>;
  sortBy: (prop: string) => void;
}

export interface IStateProps {
  patients: PatientWithAccessRightDTO[];
  currentPatient: Nullable<PatientWithAccessRightDTO>;
}

type PatientsSummaryProps = IStateProps & IOwnProps & IDispatchProps;

const PAGE_LIMIT = 25; // Default page size

export const PatientsSummary = (props: PatientsSummaryProps) => {
  const { getPatientDetailsListSuccess, patients, getPatientDetailsListError } =
    props;
  const [isMounted, setIsMounted] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [filteredPatients, setFilteredPatients] = useState<
    PatientWithAccessRightDTO[]
  >(props.patients);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [paginationState, setPaginationState] = useState({
    nextCursor: undefined as string | undefined,
    prevCursor: undefined as string | undefined,
    count: 0,
    currentPage: 1,
    perPage: PAGE_LIMIT,
    cursorStack: [] as string[], // To track navigation for "First Page"
  });
  const debounceQuerystring = useDebounce(searchTerm, DEBOUNCE_TIMEOUT);

  const handleSearch = (value: string) => {
    setSearchTerm(value);
    // Reset pagination when search changes
    setPaginationState((prev) => ({
      ...prev,
      currentPage: 1,
      cursorStack: [],
      nextCursor: undefined,
      prevCursor: undefined,
    }));
  };

  const loadPatients = async (
    nextCursor?: string,
    prevCursor?: string,
    isFirstPage: boolean = false,
    search?: string,
  ) => {
    setIsLoading(true);
    try {
      const response = await WeaverApiClientInstance.getPaginatedPatients(
        paginationState.perPage,
        nextCursor,
        prevCursor,
        props.sortOrder === 'desc' ? 'desc' : 'asc',
        props.sortProp || 'name',
        undefined, // locationId
        undefined, // program
        search,
      );

      const { data, meta } = response.data;

      getPatientDetailsListSuccess(data);

      // Update pagination state
      setPaginationState((prev: any) => {
        const newCursorStack = isFirstPage
          ? []
          : nextCursor
          ? [...prev.cursorStack, prev.prevCursor || '']
          : prev.cursorStack;

        return {
          ...prev,
          nextCursor: meta.page.nextCursor,
          prevCursor: meta.page.prevCursor,
          count: meta.page.count,
          perPage: meta.page.limit,
          currentPage: isFirstPage
            ? 1
            : nextCursor
            ? prev.currentPage + 1
            : Math.max(1, prev.currentPage - 1),
          cursorStack: newCursorStack,
        };
      });

      if (!isMounted) {
        setIsMounted(true);
      }
    } catch (e) {
      ErrorInterceptor.error(e);
      getPatientDetailsListError();
    } finally {
      setIsLoading(false);
    }
  };

  const handleNextPage = () => {
    if (paginationState.nextCursor) {
      loadPatients(
        paginationState.nextCursor,
        undefined,
        false,
        searchTerm || undefined,
      );
    }
  };

  const handlePrevPage = () => {
    if (paginationState.prevCursor) {
      loadPatients(
        undefined,
        paginationState.prevCursor,
        false,
        searchTerm || undefined,
      );
    }
  };

  const handleFirstPage = () => {
    if (paginationState.cursorStack.length > 0) {
      loadPatients(
        paginationState.cursorStack[0],
        undefined,
        true,
        searchTerm || undefined,
      );
    } else {
      loadPatients(undefined, undefined, true, searchTerm || undefined);
    }
  };

  useEffect(() => {
    return () => {
      getPatientDetailsListSuccess([]);
      setIsMounted(false);
    };
  }, [getPatientDetailsListSuccess]);

  useEffect(() => {
    loadPatients(undefined, undefined, true, searchTerm || undefined); // Initial load with search term
  }, []);

  useEffect(() => {
    if (isMounted) {
      loadPatients(undefined, undefined, true, searchTerm || undefined); // Reload with current search term when sort changes
    }
  }, [props.sortProp, props.sortOrder]);

  useEffect(() => {
    if (isMounted) {
      loadPatients(
        undefined,
        undefined,
        true,
        debounceQuerystring || undefined,
      );
    }
  }, [debounceQuerystring]);

  useEffect(() => {
    if (isMounted) {
      setFilteredPatients(patients);
    }
  }, [patients, isMounted]);

  return (
    <>
      <span data-testid="patient-summary" />
      <div styleName="header-wrapper">
        <div styleName="header-content">
          <div styleName="header-title">Patients</div>
          <SearchInput searchTerm={searchTerm} searchAction={handleSearch} />
        </div>
      </div>
      <div styleName="content-wrapper">
        <div styleName="table-wrapper">
          <Pagination
            currentPage={paginationState.currentPage}
            totalRecords={paginationState.count}
            perPage={paginationState.perPage}
            onClickNextPage={handleNextPage}
            onClickPrevPage={handlePrevPage}
            onClickFirstPage={handleFirstPage}
            dataTestId="patients-pagination"
            isLoading={isLoading}
          />
          <PatientsTable
            history={props.history}
            patients={filteredPatients}
            sortProp={props.sortProp}
            sortOrder={props.sortOrder}
            sortBy={props.sortBy}
            isLoading={isLoading}
          />
        </div>
      </div>
    </>
  );
};

const mapStateToProps = (state: IRootStateType) => {
  return {
    currentPatient: state?.currentPatient,
    patients: state.patientDetailsList,
  };
};

const mapDispatchToProps = (dispatch: Dispatch) =>
  bindActionCreators(
    {
      getPatientDetailsListSuccess,
      getPatientDetailsListError,
      clearCurrentPatient,
    },
    dispatch,
  );

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(withSort(PatientsSummary));
